import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";

interface RestorePasswordModalProps {
  open: boolean;
  onClose: () => void;
  initialEmail?: string;
  resetPasswordRequest: (email: string) => Promise<void>;
}

const RestorePasswordModal: React.FC<RestorePasswordModalProps> = ({
  open,
  onClose,
  initialEmail = "",
  resetPasswordRequest,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Update email when initialEmail changes
  React.useEffect(() => {
    if (open) {
      setEmail(initialEmail);
      setError("");
      setSuccess(false);
    }
  }, [open, initialEmail]);

  const validateEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess(false);

    if (!email) {
      setError("Por favor ingrese su correo electr�nico");
      return;
    }

    if (!validateEmail(email)) {
      setError("Por favor ingrese un correo electr�nico v�lido");
      return;
    }

    try {
      setLoading(true);
      await resetPasswordRequest(email);
      setSuccess(true);
    } catch (error: any) {
      setError(
        error.message || "Error al enviar el correo de recuperaci�n. Por favor intente nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: { xs: 1, sm: 2 },
          m: { xs: 2, sm: 3 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          textAlign="center"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          Recuperar Contraseña
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 2,
            mb: 2,
            fontSize: { xs: "0.813rem", sm: "0.875rem" },
          }}
        >
          Ingresa tu correo electrónico y te enviaremos un enlace para
          restablecer tu contraseña.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
        {success ? (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              fontSize: { xs: "0.813rem", sm: "0.875rem" },
            }}
          >
            Correo enviado exitosamente! Por favor revisa tu bandeja de
            entrada y sigue las instrucciones para restablecer tu contraseña.
            Si no lo encuentras, revisa también tu carpeta de Spam.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  fontSize: { xs: "0.813rem", sm: "0.875rem" },
                }}
              >
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              error={Boolean(error)}
              placeholder="ejemplo@correo.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "&:hover fieldset": {
                    border: "gray.200",
                  },
                  "&.Mui-focused fieldset": {
                    border: "gray.200",
                  },
                },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        {success ? (
          <Button
            onClick={handleClose}
            variant="contained"
            fullWidth
            sx={{
              py: { xs: 1, sm: 1.2 },
              backgroundColor: "secondary.main",
              "&:hover": { backgroundColor: "secondary.500" },
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Cerrar
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={loading}
              fullWidth={window.innerWidth < 600}
              sx={{
                mr: { xs: 0, sm: 1 },
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              fullWidth={window.innerWidth < 600}
              sx={{
                backgroundColor: "primary.main",
                "&:hover": { backgroundColor: "secondary.main" },
                minWidth: { xs: "100%", sm: "120px" },
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Enviar"
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RestorePasswordModal;
